/**
 * Canonical async step failure payload stored on task_step.output before engine resume.
 *
 * WHEN to call recordStepFailure / persistErrorLog:
 * - Sync engine failures (catch, StepResult.FAILED): log immediately in the step loop.
 * - Cobalt retry attempts (remainingRetries > 0): log each attempt in the webhook.
 * - Terminal async failures (CC, Cobalt final fail, Cobalt doc upload): persist on the step
 *   via mergeStepFailureOutput; log once when the engine resumes on ResumeStateType.FAILED.
 *
 * HOW: callers pass Error to insertErrorLog; the repo serializes via extractErrorDetails.
 */

export type CcErrorEntry = {
  message?: string;
  action_display_name?: string;
};

export type StepFailureOutput = {
  ccErrors?: CcErrorEntry | CcErrorEntry[] | null;
  error?: string | null;
  failureReason?: string | null;
};

/** The output keys a failure is recorded under (see {@link mergeStepFailureOutput}). */
const STEP_FAILURE_KEYS = [
  "ccErrors",
  "error",
  "failureReason",
] as const satisfies readonly (keyof StepFailureOutput)[];

/** Whether a step's output records a failure. */
export function hasStepFailure(output: Record<string, unknown> | null | undefined): boolean {
  return STEP_FAILURE_KEYS.some((key) => Boolean(output?.[key]));
}

export function mergeStepFailureOutput(
  prior: Record<string, unknown> | null | undefined,
  failure: StepFailureOutput
): Record<string, unknown> {
  return {
    ...(prior ?? {}),
    ...(failure.ccErrors != null ? { ccErrors: failure.ccErrors } : {}),
    ...(failure.error != null ? { error: failure.error } : {}),
    ...(failure.failureReason != null ? { failureReason: failure.failureReason } : {}),
  };
}

export function errorFromStepOutput(output: StepFailureOutput | null | undefined, stepKey: string): Error {
  if (!output || typeof output !== "object") {
    return new Error(`Step ${stepKey} failed`);
  }

  if (output.ccErrors) {
    const list = Array.isArray(output.ccErrors) ? output.ccErrors : [output.ccErrors];
    const message = list
      .map((entry) => {
        if (!entry || typeof entry !== "object") {
          return undefined;
        }

        return entry.message ?? entry.action_display_name;
      })
      .filter(Boolean)
      .join("; ");

    if (message) {
      return new Error(message, { cause: { ccErrors: list } });
    }
  }

  if (output.failureReason) {
    return new Error(output.failureReason, { cause: { failureReason: output.failureReason } });
  }

  if (output.error) {
    return new Error(output.error, { cause: { error: output.error } });
  }

  return new Error(`Step ${stepKey} failed`);
}
